import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { loginRequest } from './authConfig'
import { Box, SelectChangeEvent, Typography } from '@mui/material'
import LoginBanner from '../assets/banner.png'
import Logo from '../assets/icon_logo.svg'
import { buildTenantConfigs, type Config } from './configs'
import { changePca } from '../store/slices/function/pca'
import { AppDispatch, RootState } from '../store/store'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import packageJson from '../../package.json'
import { useGetCompanyTenantsQuery } from '../store/api/tenants/tenantsApi'
import { addCompany, addAuth, LoginType } from '../store/slices/function/loginSlice'
import AdminNavBar from '../components/admin/Navbar'
import AdminSideBar from '../components/admin/Sidebar'
import AdminHomePage from '../components/admin/HomePage'
import Home from '../pages/v2/home'
import CustomButton from '../components/v2/general/CustomButton/CustomButton'
import CustomSelect from '../components/v2/general/CustomSelect/CustomSelect'
import { defaultTheme, ThemeProvider } from '../components/general/layout/ThemeProvider'
import './App.module.css'
import { ModalProvider, useModal } from '../store/ModalProvider'
import CustomModal from '../components/v2/general/CustomModal/CustomModal'
import { decryptAesGcm } from '../utils/decrypt'
import { PCAHolder, getPCA } from './pca'
import Loader from '../components/general/Loader'
import Error from '../pages/error'
import { SessionTimeoutWrapper } from '../components/general/SessionTimeoutWrapper'
import { getSessionConfig } from '../config/sessionConfig'

// Export for backward compatibility
export const pca = getPCA()

// Componente interno que usa o modal dentro do ModalProvider
const AuthenticatedContent: React.FC = () => {
	const { modalData, modalTitle } = useModal()
	useEffect(() => {}, [modalTitle, modalData])

	const MemoizedHome = useMemo(() => {
		return <Home />
	}, [])

	return (
		<div>
			<CustomModal
				data={modalData}
				title={modalTitle}
			/>
			{MemoizedHome}
		</div>
	)
}

// Componente interno para resetar isLoading quando não autenticado
const ResetLoadingOnLogout: React.FC<{ onReset: () => void }> = ({ onReset }) => {
	const isAuthenticated = useIsAuthenticated()

	useEffect(() => {
		if (!isAuthenticated) {
			onReset()
		}
	}, [isAuthenticated, onReset])

	return null
}

function App(): React.ReactElement {
	const dispatch = useDispatch<AppDispatch>()
	const appPca = useSelector((state: RootState) => state.pcaSlice)
	const [company, setCompany] = useState('')
	const [error, setError] = useState(false)
	const [selectValues, setSelectValues] = useState([{ label: '', value: '' }])
	const authSlice = useSelector((state: RootState) => state.auth, shallowEqual)
	const [authType, setAuthType] = useState('backoffice')
	const [isLoading, setIsLoading] = useState(false)
	const [dynamicConfigs, setDynamicConfigs] = useState<Config>({})

	// Get session configuration - you can change the profile here
	// Options: 'development', 'production', 'securitySensitive', 'userFriendly'
	// Using 'development' for testing - change back to 'production' when ready
	const sessionConfigProfile = getSessionConfig('quality')

	// Buscar tenants da API
	const { data: tenantsData, isLoading: isLoadingTenants, error: tenantsError } = useGetCompanyTenantsQuery()

	const handleChange = (event: SelectChangeEvent<string | string[]>) => {
		const value = event.target.value as string
		setCompany(value)
		localStorage.setItem('Company', value)
		setError(false)
		dispatch(
			addCompany({
				tenantLogo: '',
				tenantValue: value,
				tenantName: '',
			}),
		)
		// Usar configs dinâmicos da API
		const configToUse = dynamicConfigs[value]
		if (configToUse) {
			dispatch(
				changePca({
					pca: new PublicClientApplication(configToUse),
				}),
			)
		} else {
			console.error(`Config não encontrado para a empresa: ${value}`)
		}
	}

	const handleLogin = async () => {
		setIsLoading(true)
		if (company === '') {
			setError(true)
			setIsLoading(false)
			return
		}
		if (error === false && company !== '' && company === localStorage.getItem('Company')) {
			await appPca.pca.loginPopup({ ...loginRequest }).catch((error: Error) => {
				console.error(error)
				setIsLoading(false)
			})
		} else {
			setIsLoading(false)
		}
	}

	const handleAuthTypeChange = (newAuthType: string) => {
		setAuthType(newAuthType)
		localStorage.removeItem('auth')
		dispatch(
			addAuth({
				...authSlice,
				isLogged: false,
				loginResponse: null,
				auth: null,
				LoginType: newAuthType as LoginType,
			}),
		)
	}

	useEffect(() => {
		PCAHolder.pca = appPca.pca
	}, [appPca.pca])

	useEffect(() => {
		setIsLoading(true)

		const version = localStorage.getItem('version')

		if (version !== packageJson.version) {
			if ('caches' in window) {
				caches.keys().then(names => {
					names.forEach(name => caches.delete(name))
				})
				window.location.reload()
			}
			localStorage.clear()
			localStorage.setItem('version', packageJson.version)
		}

		handleAuthTypeChange('backoffice')

		dispatch(
			addAuth({
				...authSlice,
				isLogged: false,
				loginResponse: null,
				auth: null,
				LoginType: authType as LoginType,
			}),
		)
		setIsLoading(false)
	}, [])

	// Processar dados da API quando disponíveis
	useEffect(() => {
		processTenants()
	}, [tenantsData, tenantsError])

	// Função memoizada para resetar isLoading
	const resetLoading = useCallback(() => {
		setIsLoading(false)
	}, [])

	const processTenants = async () => {
		if (tenantsData && tenantsData.length > 0) {
			// Criar uma cópia mutável dos dados
			const processedTenants = await Promise.all(
				tenantsData.map(async tenant => {
					const processedTenant = { ...tenant }

					// Tentar descriptografar apenas se os campos existirem e não estiverem vazios
					if (processedTenant.tenants && processedTenant.tenants.trim() !== '') {
						processedTenant.tenants = await decryptAesGcm(processedTenant.tenants)
					}
					if (processedTenant.audienceId && processedTenant.audienceId.trim() !== '') {
						processedTenant.audienceId = await decryptAesGcm(processedTenant.audienceId)
					}

					return processedTenant
				}),
			)

			// Construir configs dinâmicos a partir da API
			const configs = buildTenantConfigs(processedTenants)
			setDynamicConfigs(configs)

			// Criar valores para o select
			const sortedCompanies = processedTenants
				.filter(t => t.companyName) // Filtrar apenas tenants válidos
				.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))

			const customSelectValues = sortedCompanies.map(item => ({
				label: item.companyName || '',
				value: item.companyName || '',
			}))

			setSelectValues(customSelectValues)
		} else if (tenantsError) {
			// Erro ao carregar tenants da API
			console.error('Erro ao carregar tenants da API:', tenantsError)

			setSelectValues([])
			setDynamicConfigs({})
		}
	}

	if (isLoadingTenants) {
		return (
			<ThemeProvider theme={defaultTheme}>
				<Loader />
			</ThemeProvider>
		)
	}

	if (tenantsError) {
		return (
			<ThemeProvider theme={defaultTheme}>
				<Error />
			</ThemeProvider>
		)
	}

	if (authSlice.isLogged && authSlice.LoginType === LoginType.admin) {
		return (
			<ThemeProvider theme={defaultTheme}>
				<React.Fragment>
					<AdminNavBar />
					<AdminSideBar />
					<AdminHomePage />
				</React.Fragment>
			</ThemeProvider>
		)
	}

	return (
		<ThemeProvider theme={defaultTheme}>
			{/* na box com padding, temos de por isso adaptavel aos multiplos drawers que vai haver */}
			<MsalProvider instance={appPca.pca}>
				<ResetLoadingOnLogout onReset={resetLoading} />
				<AuthenticatedTemplate>
					<SessionTimeoutWrapper
						timeoutMinutes={sessionConfigProfile.timeoutMinutes}
						warningMinutes={sessionConfigProfile.warningMinutes}
						enabled={sessionConfigProfile.enabled}
					>
						<ModalProvider>
							{/* <PermissionHandler /> */}
							{/* <PermissionContainer> */}
							<AuthenticatedContent />
							{/* </PermissionContainer> */}
						</ModalProvider>
					</SessionTimeoutWrapper>
				</AuthenticatedTemplate>
				<UnauthenticatedTemplate>
					<Box
						component='div'
						sx={{
							position: 'relative',
							height: '100vh',
							display: 'grid',
							gridTemplateColumns: '1fr auto',
							overflow: 'hidden',
						}}
					>
						<Box
							component='div'
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								flexDirection: 'column',
								gap: '2.5rem',
							}}
						>
							<Box
								component='img'
								src={Logo}
								sx={{ height: '120px', width: '120px' }}
							/>
							<Box
								component='div'
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '1.5rem',
								}}
							>
								<Typography
									variant='h3'
									sx={{ color: 'var(--primary)', fontFamily: 'var(--font-family)' }}
								>
									Login
								</Typography>
								<Typography
									sx={{
										fontWeight: '600',
										color: 'var(--primary-dark)',
										lineHeight: '1.75',
										letterSpacing: '0.15px',
										width: '32ch',
										fontFamily: 'var(--font-family)',
									}}
									align='center'
								>
									Bem-vindo ao Transporter, selecione a Empresa onde pretende iniciar sessão
								</Typography>

								<Box
									component='div'
									sx={{ width: '100%', display: 'grid', gap: '1rem' }}
								>
									<CustomSelect
										size='small'
										label='Selecione Empresa'
										options={selectValues}
										optionsGroups={undefined}
										value={company}
										changeHandler={handleChange}
										fullWidth
										disabled={isLoading}
										error={{ state: error, message: 'Escolha uma Empresa' }}
									/>
									<CustomButton
										onClick={handleLogin}
										disabled={isLoading && !error}
										fullWidth
									>
										Entrar
									</CustomButton>
								</Box>
							</Box>
						</Box>
						<Box
							component='div'
							sx={{
								position: 'relative',
								height: '100%',
								display: 'flex',
								justifyContent: 'flex-end',
								padding: '0.5rem 0.5rem 0.5rem 1.5rem',
								overflowX: 'visible',
							}}
						>
							<Box
								component='div'
								sx={{
									position: 'relative',
									height: '100%',
									width: '755px',
									overflow: 'visible',
									borderRadius: '10px',
									backgroundColor: '#092056',
								}}
							>
								<Box
									component='img'
									src={LoginBanner}
									sx={{
										position: 'absolute',
										width: 'auto',
										height: 'auto',
										top: '50%',
										right: '-0.5rem',
										transform: 'translateY(-50%)',
									}}
								/>
								<Typography
									variant='h3'
									sx={{
										width: '19ch',
										position: 'absolute',
										bottom: '85px',
										left: '50%',
										color: '#00A5FF',
										fontWeight: '600',
										fontSize: '2.25rem',
										transform: 'translateX(-50%)',
										fontFamily: 'var(--font-family)',
									}}
								>
									A viagem começa aqui.
								</Typography>
							</Box>
						</Box>
					</Box>
				</UnauthenticatedTemplate>
			</MsalProvider>
		</ThemeProvider>
	)
}
export default App
